// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, Upload, Loader2, FileSpreadsheet, CheckCircle2, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";

export default function InterviewImportPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const handleImport = async () => {
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await interviewAPI.importQuestions(workspaceId, user?.id, formData);
      setResult(res.data);
      if (res.data.imported_count > 0) {
        toast.success(`${res.data.imported_count} questions importées`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'import");
    } finally { setUploading(false); }
  };

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews/questions`)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Import Excel</h1>
            <p className="text-sm text-muted-foreground">Importez vos questions d'entretien depuis un fichier Excel</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Format Info */}
          <Card>
            <CardHeader><CardTitle className="text-base">Format attendu</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Le système détecte automatiquement les colonnes. Voici les en-têtes reconnus :
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2"><Badge variant="outline">question / intitulé</Badge><span className="text-muted-foreground">Texte de la question</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">réponse / answer</Badge><span className="text-muted-foreground">Réponse attendue</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">domaine / domain</Badge><span className="text-muted-foreground">Domaine technique</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">difficulté / level</Badge><span className="text-muted-foreground">junior, confirmé, senior, expert</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">type</Badge><span className="text-muted-foreground">ouverte, qcm, situation...</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">score / points</Badge><span className="text-muted-foreground">Score maximum</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">poids / weight</Badge><span className="text-muted-foreground">Coefficient</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline">tags</Badge><span className="text-muted-foreground">Tags séparés par virgules</span></div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                💡 Si aucun en-tête n'est détecté, le <strong>nom de l'onglet</strong> sera utilisé comme domaine et la première colonne comme texte de question.
              </p>
              <div className="flex justify-center mt-4 pt-3 border-t">
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    window.open(`${backendUrl}/api/interviews/questions/template`, '_blank');
                  }}
                >
                  <Download className="h-4 w-4" />
                  Télécharger le template Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upload Area */}
          <Card>
            <CardContent className="pt-6">
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
                  ${dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
              >
                <FileSpreadsheet className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                {file ? (
                  <div>
                    <p className="font-semibold text-lg">{file.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} Ko</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-lg">Glissez votre fichier Excel ici</p>
                    <p className="text-sm text-muted-foreground mt-1">ou cliquez pour parcourir (.xlsx, .xls)</p>
                  </div>
                )}
                <input id="file-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
              </div>

              <div className="flex justify-center mt-6">
                <Button onClick={handleImport} disabled={!file || uploading} className="gap-2 px-8">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Import en cours..." : "Importer"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Result */}
          {result && (
            <Card className={result.imported_count > 0 ? "border-green-200 bg-green-50/50" : "border-yellow-200 bg-yellow-50/50"}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  {result.imported_count > 0 ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  ) : (
                    <AlertCircle className="h-8 w-8 text-yellow-600" />
                  )}
                  <div>
                    <p className="font-semibold text-lg">{result.imported_count} question(s) importée(s)</p>
                    <p className="text-sm text-muted-foreground">
                      Onglets traités : {result.sheets_processed?.join(", ")}
                    </p>
                  </div>
                </div>

                {result.errors?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-red-600 mb-2">{result.errors.length} erreur(s) :</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {result.errors.map((err, i) => (
                        <p key={i} className="text-xs text-muted-foreground">
                          Ligne {err.row} ({err.sheet}) : {err.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>Importer un autre fichier</Button>
                  <Button onClick={() => navigate(`/workspace/${workspaceId}/interviews/questions`)}>Voir les questions</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </WorkspaceLayout>
  );
}
